import {ColumnDef} from "@tanstack/react-table";
import {sortableColumnHeader} from "@/components/data-table.tsx";
import {Link} from "@/components/tailwind/link.tsx";
import {Text} from "@/components/tailwind/text"
import {SquareUserRoundIcon} from "lucide-react";
import {Driver, DriverPhoneNumber} from "@/models/driver.ts";
import PhoneNumberDisplay from "@/components/phone-number-display.tsx";
import InlineEditableCell from "@/components/inline-editable-cell.tsx";
import {useTranslation} from "react-i18next";

function generateNameColumnContent(row: Driver) {
    const nameContent = [row.salutation, row.title, row.firstName, row.lastName].filter(Boolean).join(" ")
    return <Text>{nameContent}</Text>
}

function generatePhoneColumnContent(phoneNumbers?: DriverPhoneNumber[]) {
    if (!Array.isArray(phoneNumbers) || !phoneNumbers?.length) {
        return (<></>)
    }

    return (
        <section role="list">
            {phoneNumbers.map((entry, index) => (
                    <PhoneNumberDisplay type={entry.type} number={entry.number} key={index}/>
                )
            )}
        </section>
    )
}

function constructSortableNameValue(row: Driver) {
    return `${row.lastName || ''}, ${row.firstName || ''} ${row.title || ''} ${row.salutation || ''}`.trim();
}

interface DriverColumnsProps {
    onCurrentNotesUpdate: (driverId: string, currentNotes: string | null) => Promise<void>
}

function CurrentNotesCell({driver, onCurrentNotesUpdate}: {
    driver: Driver,
    onCurrentNotesUpdate: (driverId: string, currentNotes: string | null) => Promise<void>
}) {
    const {t} = useTranslation('reports')
    return (
        <InlineEditableCell
            value={driver.currentNotes}
            onSave={(newValue) => onCurrentNotesUpdate(driver.id, newValue)}
            placeholder={t('drivers.table.headers.currentNotes')}
        />
    )
}

export function createDriverColumns({onCurrentNotesUpdate}: DriverColumnsProps): ColumnDef<Driver>[] {
    return [
        {
            accessorKey: "memberNo",
            header: ({column}) =>
                sortableColumnHeader(column, "drivers.table.headers.memberNo", "reports")
        },
        {
            id: "name",
            accessorFn: constructSortableNameValue,
            header: ({column}) =>
                sortableColumnHeader(column, "drivers.table.headers.name", "reports"),
            cell: ({row}) => generateNameColumnContent(row.original),
        },
        {
            accessorKey: "district",
            header: ({column}) => sortableColumnHeader(column, "drivers.table.headers.district", "reports"),
            cell: ({row}) => {
                const district = row.original.district
                if (!district) {
                    return <Text className="italic text-gray-500">-</Text>
                }
                return <Text>{district}</Text>
            }
        },
        {
            id: "phoneNumbers",
            header: ({column}) => sortableColumnHeader(column, "drivers.table.headers.phoneNumbers", "reports"),
            cell: ({row}) => generatePhoneColumnContent(row.original.phoneNumbers)
        },
        {
            accessorKey: "email",
            header: ({column}) => sortableColumnHeader(column, "drivers.table.headers.email", "reports"),
            cell: ({row}) => {
                const email = row.original.email
                if (!email) {
                    return <Text className="italic text-gray-500">-</Text>
                }
                return <Text>{email}</Text>
            }
        },
        {
            accessorKey: "address",
            header: ({column}) => sortableColumnHeader(column, "drivers.table.headers.address", "reports"),
            cell: ({row}) => {
                return (
                    <address>
                        <Text>{row.original.street} {row.original.houseNumber}</Text>
                        <Text>{row.original.postcode} {row.original.city}</Text>
                    </address>
                )
            }
        },
        {
            accessorKey: "availabilityComments",
            header: ({column}) => sortableColumnHeader(column, "drivers.table.headers.availabilityComments", "reports"),
            cell: ({row}) => {
                const comments = row.original.availabilityComments
                if (!comments) {
                    return <Text className="italic text-gray-500">-</Text>
                }
                return <Text className="whitespace-pre-wrap">{comments}</Text>
            }
        },
        {
            id: "currentNotes",
            accessorKey: "currentNotes",
            header: ({column}) => sortableColumnHeader(column, "drivers.table.headers.currentNotes", "reports"),
            cell: ({row}) => (
                <CurrentNotesCell
                    driver={row.original}
                    onCurrentNotesUpdate={onCurrentNotesUpdate}
                />
            )
        },
        {
            id: "actions",
            cell: ({row}) => {
                const driver = row.original

                return (
                    <Link
                        href={`/members/${driver.id}?from=drivers`}
                        data-testid={`driver-details-link-${driver.id}`}
                        aria-label={`View details for driver ${driver.memberNo}`}
                    >
                        <SquareUserRoundIcon/>
                    </Link>
                )
            }
        }
    ]
}
